import {isResponseError} from '@github-ui/react-core/future/response-error'
import {AgentMergePresentation_Kind as Kind} from '@github-ui/dotcom-schema/types/copilot_sessions/v1/agent_merge'
import {useAgentMergeResolvedStatus} from './use-agent-merge-resolved-status'
import {useAgentMergeEnrollmentDisplayState} from './use-agent-merge-enrollment-display-state'
import {AgentMergePolicyDisabledError, unresolvedAgentMergeEnrollment} from '../page-data/payloads/agent-merge'
import {
  useAgentMergePageData,
  useInvalidateAgentMergeConfigurationOnAliveUpdate,
} from '../page-data/loaders/use-agent-merge-page-data'

function isReadDenied(error: Error | null) {
  const status = isResponseError(error) ? error.response.status : error?.cause
  return typeof status === 'number' && [401, 403, 404].includes(status)
}

export function useAgentMergeAssigneeSession({
  enabled,
  currentHeadSha,
  pullRequestChannel,
  viewerLogin,
}: {
  enabled: boolean
  currentHeadSha: string
  pullRequestChannel?: string
  viewerLogin?: string | null
}) {
  const configuration = useAgentMergePageData({enabled, currentHeadSha})
  const {data, error} = configuration
  const readDenied = error instanceof AgentMergePolicyDisabledError || isReadDenied(error)
  const enrollment = useAgentMergeEnrollmentDisplayState(unresolvedAgentMergeEnrollment(data, error))
  const pendingEnrollment = enrollment === 'pending'
  const policyDisabled = data?.automationsPolicyDisabled === true
  const statusOnly = policyDisabled && data?.evaluationStatusAvailable === true && data.displayGeneration !== undefined
  const isEnrolled = enabled && data?.enabled === true && !policyDisabled && !pendingEnrollment
  const tracking =
    enabled &&
    !readDenied &&
    !pendingEnrollment &&
    (!policyDisabled || statusOnly) &&
    (isEnrolled || data?.evaluationStatusAvailable === true)
  const resolvedEnabled = tracking && data?.evaluationStatusAvailable === true
  const resolved = useAgentMergeResolvedStatus({
    enabled: resolvedEnabled,
    scope: data?.evaluationScope,
    configurationHeadOid: currentHeadSha,
    revision: data?.etag ?? '',
    configurationAvailable: !configuration.isError,
    configurationReadDenied: readDenied,
    configurationEnabled: data?.enabled === true,
    pullRequestChannel,
    viewerLogin,
  })
  useInvalidateAgentMergeConfigurationOnAliveUpdate(enabled ? pullRequestChannel : undefined, currentHeadSha)

  const sessionError = resolvedEnabled ? resolved.error : null
  const expectedState = readDenied || pendingEnrollment || isReadDenied(sessionError)
  const reader = resolvedEnabled ? resolved : null
  const isPending =
    enabled &&
    !readDenied &&
    !isReadDenied(sessionError) &&
    (pendingEnrollment ||
      configuration.isPending ||
      (configuration.isFetching && (configuration.isError || Boolean(sessionError))) ||
      reader?.isPending === true ||
      (reader?.isFetching === true && Boolean(sessionError)))
  if (isPending) return {session: null, isError: false, isWatching: false, isPending: true}
  const isError =
    enabled &&
    !expectedState &&
    (configuration.isError ||
      (resolvedEnabled ? resolved.presentation.row === 'read_unavailable' : Boolean(sessionError)))
  if (!tracking || expectedState || isError) return {session: null, isError, isWatching: false, isPending: false}

  if (resolvedEnabled) {
    const {url, goal, message} = resolved.presentation
    const status = message?.kind === Kind.BUSY ? ('running' as const) : ('idle' as const)
    return {
      session: url ? {status, url, goal, description: message?.message} : null,
      message: message?.message,
      kind: message?.kind,
      isError,
      isPending: false,
      isWatching:
        isEnrolled &&
        !url &&
        !message &&
        (resolved.presentation.row === 'baseline' || resolved.presentation.row === 'off'),
    }
  }

  return {session: null, isError, isPending: false, isWatching: false}
}
